import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { FilterProvider } from './context/FilterContext';
import DashboardLayout from './components/layout/DashboardLayout';
import ComingSoon from './components/ComingSoon';
import { queryClient } from './lib/queryClient';
import { useStoreValidation } from './hooks/useStoreValidation';
import './App.css';

const StoreIdRequired: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="store-id-required">
      <div className="store-id-message">
        <h2>🏪 {t('storeIdRequired')}</h2>
        <p>{t('storeIdGuidance')}</p>
        <div className="example-url">
          <p><strong>{t('example')}</strong></p>
          <code>
            {window.location.origin}/?storeId=a0JPC00000cKHlB2AW
          </code>
        </div>
        <div className="help-text">
          <p>{t('contactAdmin')}</p>
        </div>
        <button 
          onClick={() => window.location.reload()}
          className="refresh-button"
        >
          {t('refresh')}
        </button>
      </div>
    </div>
  );
};

const InitialLoading: React.FC = () => {
  const { t } = useTranslation(); 
  return (
    <div className="store-checker-loading">
      <div className="loading-spinner"></div>
      <p>{t('initializing')}</p>
    </div>
  );
};

const StoreValidationLoading: React.FC<{ storeId: string }> = ({ storeId }) => {
  const { t } = useTranslation();
  return (
    <div className="store-checker-loading">
      <div className="loading-spinner"></div>
      <p>{t('validatingStore', { storeId }) || `Validating store ${storeId}...`}</p>
    </div>
  );
};



const DashboardWithValidation: React.FC<{ storeId: string }> = ({ storeId }) => {
  const { 
    storeStatus, 
    storeValidating
  } = useStoreValidation(storeId);

  
  if (storeValidating) {
    return <StoreValidationLoading storeId={storeId} />;
  }

  
  if (storeStatus) {
    switch (storeStatus.status) {
      case 'no-store':
      case 'invalid':
        return (
          <ComingSoon 
            storeId={storeId} 
            reason="no-store" 
          />
        );
      
      case 'no-data':
        return (
          <ComingSoon 
            storeId={storeId} 
            reason="no-data" 
          />
        );
      
      case 'error':
        return (
          <ComingSoon 
            storeId={storeId} 
            reason="no-store" 
          />
        );
      
      case 'ready':
        
        return (
          <QueryClientProvider client={queryClient}>
            <FilterProvider>
              <DashboardLayout />
            </FilterProvider>
            <ReactQueryDevtools 
              initialIsOpen={false} 
              position="bottom" 
            />
          </QueryClientProvider>
        );
      
      default:
        return (
          <ComingSoon 
            storeId={storeId} 
            reason="no-store" 
          />
        );
    }
  }

  
  return <StoreValidationLoading storeId={storeId} />;
};

function App() {
  const [storeId, setStoreId] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkStoreId = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const currentStoreId = urlParams.get('storeId');
      setStoreId(currentStoreId);
      setIsChecking(false);
    };

    checkStoreId();

    const handlePopState = () => {
      checkStoreId();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  if (isChecking) {
    return (
      <div className="App">
        <InitialLoading />
      </div>
    );
  }

  if (!storeId) {
    return (
      <div className="App">
        <StoreIdRequired />
      </div>
    );
  }

  return (
    <div className="App">
      <DashboardWithValidation storeId={storeId} />
    </div>
  );
}

export default App;